/**
 * Area Listings Page V2 View
 *
 * Pure render component - NO hooks, only JSX composition.
 * Receives data and handlers from Controller.
 * 
 * For now, this is identical to the legacy view. It will be refactored
 * to follow the Controller/View pattern once the v2 layout is designed.
 */

import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import Breadcrumbs from 'components/breadcrumbs';
import Button, { PRIMARY_THEME, TERTIARY_THEME } from 'components/control/button';
import SelectField from 'components/control/select-field';
import MapIcon from 'components/icon/map-icon';
import { LocalizedInternalLinks } from 'components/home-page/internal-links';
import FilterIcon from 'components/icon/filter-icon';
import ListingsGrid from 'components/listings-grid';
import { SurroundingCities } from 'components/related-searches';
import { headerIds, searchFilterIds, testIds } from 'constants/test-constants';
import { AVAILABLE_STATUS, Filter, Sort } from 'contexts/preferences/listing-params/types';
import { useIsMobileV2 } from 'hooks/use-breakpoint-v2';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import prioritizeFeeds from 'utils/prioritize-feeds';
import { getLocalizedSortOptions } from 'utils/localized-select-options';
import ProviderDisclaimer from '../../../provider-disclaimer';
import styles from './style.module.scss';
import dynamic from 'next/dynamic';
import { InsightsResponse, SingleAddress } from '@zoocasa/go-search';
import AreaGuides from '../components/guides/area-guides';
import { NamedContents } from 'components/named-content';
import { ListingFilterBar } from 'components/listing-filter-bar';
import { FilterChipBar } from 'components/listing-filter-bar/filter_chip_bar';
import { FilterItemData, getActiveFilters, SavedSearchButton } from 'components/listing-filter-bar/listing_filter_bar';
import { useOnListingCardClick } from 'utils/listing-card';
import { Namespaces, LOCALE_COMMON_PATHS, LOCALE_SEARCH_PATHS } from 'types/locales';
import { translatePropertyType } from '../util/translate-property-type';
import { useListingCardClickWithAnalytics } from 'hooks/use-listing-card-click';
import { GTM_CLICK_LISTING_CARD } from 'constants/events';
import { ResponsiveContainer } from 'components/atomic/atoms/responsive-container';
import { MobileListingFilterBarV2 } from 'ui/mobile-filter-bar-v2/mobile-filter-bar-v2';
import useDebounce from 'hooks/use-debounce';
import { buildClassName } from 'utils/build-class-name';
import type { Breadcrumb } from 'components/breadcrumbs';
import type { LocalizedLinkData } from 'components/pages/area-listings/generate-dynamic-footer-data';
import type { ListingCardData } from 'components/listing-card/listing-card';
import type { ListingParams } from 'contexts/preferences/listing-params/types';
import type SearchPrediction from 'data/search-predictions';
import type { PartialDeep } from 'type-fest';
import type { LocalizedSeoLinkData } from 'data/seo-links';
import type { ListingCardDataWithCompliance } from 'utils/listing-card/listing-card-transformers';

const SearchFilterDeprecationWarning = dynamic(() => import('components/search-filter-deprecation-warning'), { ssr: false });
const LocalizedProvinceCitySeoLinks = dynamic(() => import('../components/province-city-seo-links').then(mod => ({ default: mod.LocalizedProvinceCitySeoLinks })));

const ProviderDisclaimerComponent = React.memo(
  ({ providerIds }: { providerIds: number[] }) => {
    return providerIds.map(p => <ProviderDisclaimer key={p} providerId={p} tenantName={'zoocasa'}/>);
  },
  (prevProps, nextProps) => {
    // Check if arrays have the same length
    if (prevProps.providerIds.length !== nextProps.providerIds.length) {
      return false;
    }
    // Check if all elements are the same
    return prevProps.providerIds.every((id, index) => id === nextProps.providerIds[index]);
  }
);

//#region Types
export interface Pagination {
  page: number;
  size: number;
  count: number;
  total: number;
}

export type AreaInsightsData = {
  areaData?: InsightsResponse;
  surroundingCities?: SurroundingCities[];
};

export interface LocalizedHeadingData {
  key: string;
  values: {
    propertyType: string;
    areaName?: string;
  };
}

export interface LocalizedSubheadingData {
  key: string;
  values: {
    count?: number;
    requestedArea?: string;
    electedArea?: string;
  };
  count?: number;
}

export interface AreaListingsPageV2ViewProps {
  electedAddress: SingleAddress;
  listingParams: ListingParams;
  listings: readonly ListingCardData[];
  listingsWithCompliance?: readonly ListingCardDataWithCompliance[];
  useListingCardWithCompliance: boolean;
  breadcrumbs: readonly Breadcrumb[];
  pagination: Pagination;
  sort: Sort;
  heading: string;
  localizedHeading?: LocalizedHeadingData;
  localizedSubheading?: LocalizedSubheadingData;
  areaName?: string;
  areaBlurb?: string;
  areaBlurbHeading?: string;
  areaOverviewTitle?: string;
  areaNamedContent?: NamedContents;
  areaInsightsData?: AreaInsightsData;
  showMapCardExperimentView?: boolean;
  internalLinks?: LocalizedLinkData[];
  internalLinksHeading?: string;
  seoLinks?: LocalizedSeoLinkData;
  noListingsInAreaMessage?: string;
  hasNoListingsInArea?: boolean;
  missingAddressMessage?: string;
  expandGuidesByDefault?: boolean;
  showGuides?: boolean;
  showDisclaimer?: boolean;
  showFilterDeprecationWarning: boolean;
  showNoImageListingsFilterWarning: boolean;
  showsMyLinkMyLeadCreate: boolean;
  setPageNumber: (pageNumber: number) => void;
  onMobileFilterButtonClick(): void;
  onSortChanged(sort: Sort): void;
  onValueChange: (filters: PartialDeep<Filter>, changedFilter?: string) => void;
  onSaveSearchButtonClick: () => void;
  onLocationSearchClick: (searchPrediction: SearchPrediction) => void;
  onMapIconClick: () => void;
  onMoreButtonClick: () => void;
  onDeprecationWarningButtonClick?: () => void;
  onNoImageListingsFilterWarningClose?: () => void;
  trackMapCardExperimentView?: () => void;
  trackMapCardClick?: () => void;
  // Analytics props injected by v2 controller
  onFilterButtonClickAnalytics?: () => void;
  onSortChangedAnalytics?: (sort: Sort) => void;
  onSaveSearchButtonClickAnalytics?: () => void;
}
//#endregion

//#region Constants
const SearchNavClassName = styles['search-nav'];
const TitlePaddingClassName = styles['title-padding'];
const TitleSortRowClassName = styles['title-sort-row'];
const PageTitleClassName = styles['page-title'];
const PageSubtitleClassName = styles['page-subtitle'];
const MissingAddressClassName = styles['missing-address'];
const SubFilterClassName = styles['sub-filter'];
const SortByClassName = styles['sort-by'];
const MobileFilterBtnClassName = styles['mobile-filter-btn'];
//#endregion

/**
 * AreaListingsPageV2View
 *
 * Pure render component. Receives all data and handlers from Controller.
 * Responsible for:
 * - Composing sections with data and handlers
 * - Responsive layout (mobile vs desktop)
 */
export function AreaListingsPageV2View(props: AreaListingsPageV2ViewProps) {
  const {
    useListingCardWithCompliance,
    listingParams,
    listings,
    listingsWithCompliance, // V2 listings with server-side computed compliance
    breadcrumbs,
    pagination: {
      page, count, total, size,
    },
    sort,
    heading,
    localizedHeading,
    localizedSubheading,
    areaName,
    areaBlurb,
    areaBlurbHeading,
    areaInsightsData,
    areaNamedContent,
    areaOverviewTitle,
    showMapCardExperimentView,
    internalLinks,
    internalLinksHeading,
    seoLinks,
    noListingsInAreaMessage,
    hasNoListingsInArea,
    missingAddressMessage,
    expandGuidesByDefault,
    showGuides,
    showDisclaimer,
    showFilterDeprecationWarning,
    showNoImageListingsFilterWarning,
    showsMyLinkMyLeadCreate,
    setPageNumber,
    onSortChanged,
    onMobileFilterButtonClick,
    onValueChange,
    onSaveSearchButtonClick,
    onLocationSearchClick,
    onMapIconClick,
    onMoreButtonClick,
    onDeprecationWarningButtonClick = () => { /* no-op */ },
    onNoImageListingsFilterWarningClose = () => { /* no-op */ },
    trackMapCardExperimentView,
    trackMapCardClick,
    onFilterButtonClickAnalytics,
    onSortChangedAnalytics,
    onSaveSearchButtonClickAnalytics,
  } = props;
  const { t: tSearch } = useTranslation(Namespaces.SEARCH);
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatNumber } = useI18nFormatting();
  const onListingCardClick = useOnListingCardClick();
  // Use v2 click handler with analytics tracking for new listing cards
  const onListingCardClickWithCompliance = useListingCardClickWithAnalytics({
    onCardClick: GTM_CLICK_LISTING_CARD,
    onAddressClick: 'Xp_UiAddresspView',
  });
  const isMobile = useIsMobileV2();
  const [activeFilterItems, setActiveFilterItems] = useState<FilterItemData[]>([]);
  const filterBarIsOpen = activeFilterItems.length !== 0;
  const providerIds = showDisclaimer ? prioritizeFeeds([...new Set(listings.map(l => l.providerId))]) : [];
  
  // Scroll visibility state for mobile filter bar
  const [isFilterBarVisible, setIsFilterBarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Scroll threshold - header will only hide/show after scrolling past this point
  const SCROLL_THRESHOLD = 100;
  
  // Generate localized heading with translated property types
  // Note: localizedHeading.key comes from server and is validated at runtime via defaultValue fallback
  const displayHeading = localizedHeading 
    ? capitalizeWords(t(localizedHeading.key, { 
      ...localizedHeading.values, 
      propertyType: translatePropertyType(localizedHeading.values.propertyType, t),
      defaultValue: heading, 
    }))
    : heading;
  
  // Generate localized subheading with proper pluralization
  const displaySubheading = localizedSubheading
    ? capitalizeWords(t(localizedSubheading.key, { 
      ...localizedSubheading.values, 
      count: localizedSubheading.count,
      defaultValue: hasNoListingsInArea ? noListingsInAreaMessage : `${formatNumber(count)}+ listings found`,
    }))
    : hasNoListingsInArea 
      ? noListingsInAreaMessage 
      : capitalizeWords(t(LOCALE_COMMON_PATHS.AREA_PAGE_SUBHEADING_LISTINGS_FOUND, {
        count,
        defaultValue: `${formatNumber(count)}+ listings found`,
      }));
  
  const displayHeadingWithCount = hasNoListingsInArea ? displayHeading : `${formatNumber(count)}+ ${displayHeading}`;
  const listingsGridBreadcrumbs = [...breadcrumbs];
  const listingsGridParams = {
    page: {
      number: page,
      size: size,
    },
  };
  const listingsGridMeta = {
    totalCount: count,
    totalPages: total === 200 || total === 0 ? total : total + 1,
  };

  const sortingOptions = useMemo(() => getLocalizedSortOptions(listingParams.filter.status || AVAILABLE_STATUS, tSearch), [listingParams.filter.status, tSearch]);
  const showInternalLinks = internalLinks?.length > 0;
  const showSeoLinks = seoLinks?.links?.length > 0;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActiveFilterItems(getActiveFilters(listingParams.filter));
  }, [listingParams.filter]);

  // Scroll handler for mobile filter bar hide/show behavior
  const handleFilterBarScroll = () => {
    const currentScrollY = window.scrollY;
    
    // Only apply hide/show logic if user has scrolled past the threshold
    if (currentScrollY > SCROLL_THRESHOLD) {
      if (currentScrollY > lastScrollY) {
        setIsFilterBarVisible(false);
      } else {
        setIsFilterBarVisible(true);
      }
    } else {
      // Keep header visible if scroll position is below threshold
      setIsFilterBarVisible(true);
    }

    setLastScrollY(currentScrollY);
  };
  const debouncedFilterBarScroll = useDebounce(handleFilterBarScroll, 50);

  // Attach scroll listener for mobile filter bar
  useEffect(() => {
    window.addEventListener('scroll', debouncedFilterBarScroll, { passive: true });
    return () => window.removeEventListener('scroll', debouncedFilterBarScroll);
  }, [debouncedFilterBarScroll]);

  return (
    <div>
      <ResponsiveContainer show="mobile-small" renderMode="conditional">
        <div className={styles.component}>
          <div className={buildClassName(styles['mobile-filter-bar-sticky'], isFilterBarVisible ? styles['is-visible'] : styles['is-hidden'])}>
            <MobileListingFilterBarV2
              sort={sort}
              sortingOptions={sortingOptions}
              onSortChanged={onSortChanged}
              onMobileFilterButtonClick={onMobileFilterButtonClick}
              showsMyLinkMyLeadCreate={showsMyLinkMyLeadCreate}
              showSelectDropdown={true}
              hasFiltersSet={filterBarIsOpen}
              onSaveSearchButtonClick={onSaveSearchButtonClick}
              onFilterButtonClickAnalytics={onFilterButtonClickAnalytics}
              onSortChangedAnalytics={onSortChangedAnalytics}
              onSaveSearchButtonClickAnalytics={onSaveSearchButtonClickAnalytics}
            />
          </div>
          <div className={TitleSortRowClassName}>
            <Breadcrumbs breadcrumbs={breadcrumbs} className={styles['breadcrumbs']}/>
            <div>
              <h1 className={PageTitleClassName} data-testid={headerIds.pageHeading}>
                {displayHeadingWithCount}
              </h1>
              {missingAddressMessage && (
                <h2 className={MissingAddressClassName}>
                  {missingAddressMessage}
                </h2>
              )}
            </div>
          </div>
          <ListingsGrid
            testId={testIds.listingGrid}
            listings={listings}
            listingsWithCompliance={listingsWithCompliance}
            useListingCardWithCompliance={useListingCardWithCompliance}
            isLoading={false}
            params={listingsGridParams}
            meta={listingsGridMeta}
            showMapCardExperimentView={showMapCardExperimentView}
            breadcrumbs={listingsGridBreadcrumbs}
            setPageNumber={setPageNumber}
            listingParams={listingParams}
            trackMapCardExperimentView={trackMapCardExperimentView}
            trackMapCardClick={trackMapCardClick}
            onListingCardClick={onListingCardClick}
            onListingCardClickWithCompliance={onListingCardClickWithCompliance}
          />
          {showGuides &&
            <AreaGuides
              areaOverviewTitle={areaOverviewTitle}
              areaName={areaName}
              areaBlurbHeading={areaBlurbHeading}
              areaBlurb={areaBlurb}
              namedContent={areaNamedContent}
              isActive={expandGuidesByDefault}
              insightsData={areaInsightsData?.areaData}
            />
          }
          {showDisclaimer && <ProviderDisclaimerComponent providerIds={providerIds}/>}
          {showInternalLinks &&
        <LocalizedInternalLinks isActive={expandGuidesByDefault} data={internalLinks} heading={internalLinksHeading} />
          }
          {showSeoLinks && <LocalizedProvinceCitySeoLinks seoLinks={seoLinks} />}
        </div>
        {showFilterDeprecationWarning &&
        <SearchFilterDeprecationWarning onClose={onDeprecationWarningButtonClick}/>
        }
        <div className={styles['mobile-map-button-container']}>
          <Button
            Icon={MapIcon}
            theme={PRIMARY_THEME}
            label={tSearch(LOCALE_SEARCH_PATHS.FILTER_VIEW_MAP, { defaultValue: 'Map' })}
            onClick={onMapIconClick}
            className={styles['mobile-map-button']}
          />
        </div>
      </ResponsiveContainer>
      <ResponsiveContainer show="tablet-small-up" renderMode="conditional">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <div className={styles.component}>
          <section className={SearchNavClassName}>
            <ListingFilterBar
              onLocationSearchClick={onLocationSearchClick}
              activeFilterItems={activeFilterItems}
              setActiveFilterItems={setActiveFilterItems}
              onValueChange={onValueChange}
              onSaveSearchButtonClick={onSaveSearchButtonClick}
              onMapIconClick={onMapIconClick}
              onMoreButtonClick={onMoreButtonClick}
              filters={listingParams.filter}
              showNoImageListingsFilterWarning={showNoImageListingsFilterWarning}
              onNoImageListingsFilterWarningClose={onNoImageListingsFilterWarningClose}
              showMLMLCreateInFilterBar={showsMyLinkMyLeadCreate}
            />
          </section>
          <div className={TitlePaddingClassName} />
          <div className={TitleSortRowClassName}>
            <div>
              <h1 className={PageTitleClassName} data-testid={headerIds.pageHeading}>
                {displayHeading}
              </h1>
              {missingAddressMessage && (
                <h2 className={MissingAddressClassName}>
                  {missingAddressMessage}
                </h2>
              )}
              <h2 className={PageSubtitleClassName}>
                {displaySubheading}
              </h2>
            </div>
            <div className={SubFilterClassName}>
              <SelectField
                className={SortByClassName}
                testId={searchFilterIds.sortByDropdown}
                ariaLabel={'Sort by dropdown'}
                value={sort}
                options={sortingOptions}
                onValueChange={onSortChanged}
              />
              <Button
                className={MobileFilterBtnClassName}
                testId={searchFilterIds.mobileFilterButton}
                theme={TERTIARY_THEME}
                Icon={FilterIcon}
                label="Filters"
                onClick={onMobileFilterButtonClick}
              />
              {!showsMyLinkMyLeadCreate && (isMobile || !isMobile && !filterBarIsOpen) ?
                <SavedSearchButton onSaveSearchButtonClick={onSaveSearchButtonClick} /> :
                null}
            </div>
            {(isMobile && filterBarIsOpen) && <div className={styles['mobile-filter-chip-bar']}>
              <FilterChipBar
                activeFilterItems={activeFilterItems}
                setActiveFilterItems={setActiveFilterItems}
                onValueChange={onValueChange}
                onSaveSearchButtonClick={onSaveSearchButtonClick}
                filters={listingParams.filter}
                isMobile={isMobile}
              />
            </div>}
          </div>
          <ListingsGrid
            testId={testIds.listingGrid}
            listings={listings}
            listingsWithCompliance={listingsWithCompliance}
            useListingCardWithCompliance={useListingCardWithCompliance}
            isLoading={false}
            params={listingsGridParams}
            meta={listingsGridMeta}
            showMapCardExperimentView={showMapCardExperimentView}
            breadcrumbs={listingsGridBreadcrumbs}
            setPageNumber={setPageNumber}
            listingParams={listingParams}
            trackMapCardExperimentView={trackMapCardExperimentView}
            trackMapCardClick={trackMapCardClick}
            onListingCardClick={onListingCardClick}
            onListingCardClickWithCompliance={onListingCardClickWithCompliance}
          />
          {showGuides &&
            <AreaGuides
              areaOverviewTitle={areaOverviewTitle}
              areaName={areaName}
              areaBlurbHeading={areaBlurbHeading}
              areaBlurb={areaBlurb}
              namedContent={areaNamedContent}
              isActive={expandGuidesByDefault}
              insightsData={areaInsightsData?.areaData}
            />
          }
          {showDisclaimer && <ProviderDisclaimerComponent providerIds={providerIds}/>}
          {showInternalLinks &&
        <LocalizedInternalLinks isActive={expandGuidesByDefault} data={internalLinks} heading={internalLinksHeading} />
          }
          {showSeoLinks && <LocalizedProvinceCitySeoLinks seoLinks={seoLinks} />}
        </div>
        {showFilterDeprecationWarning &&
        <SearchFilterDeprecationWarning onClose={onDeprecationWarningButtonClick}/>
        }
      </ResponsiveContainer>
    </div> );
}

export default AreaListingsPageV2View;
